/**
 * Communities Content Component
 * Display a grid of community cards with "View More" (5 rows initially)
 */

"use client";

import {type JSX, useEffect} from "react";

import SmartImage from "@/shared/ui/smart-image";

import {DestinationCard} from "@/entities/destination/ui";

import {useModel} from "@/shared/hooks";
import {useMatchMedia} from "@/shared/hooks/dom/use-match-media";
import {useTranslation} from "@/shared/i18n";
import {ButtonBase} from "@/shared/ui/buttons/button-base";
import {ContainerBase} from "@/shared/ui/layout/container-base";
import {assetPath} from "@/shared/utils/asset-path";

import type {CommunityCardData} from "../communities-archive.controller";
import {CommunitiesModel} from "../communities.model";

const ROWS_PER_PAGE = 5;

export interface CommunitiesContentProps {
    communities: Array<CommunityCardData>;
}

export function CommunitiesContent({communities}: CommunitiesContentProps): JSX.Element {
    const {t} = useTranslation("common");
    const [model, snap] = useModel(CommunitiesModel, {communities});

    // Responsive: determine columns based on viewport breakpoints (matching CSS grid)
    const isDesktop = useMatchMedia({maxWidth: 1024}); // <=1024px: ~3 columns
    const isMobile = useMatchMedia({maxWidth: 768}); // <=768px: 2 columns

    useEffect(() => {
        let columns = 4; // Default: >1024px
        if (isMobile) {
            columns = 2;
        } else if (isDesktop) {
            columns = 3;
        }
        model.setItemsPerPage(ROWS_PER_PAGE * columns);
    }, [isDesktop, isMobile]);

    return (
        <section className="communities-archive-grid">
            <ContainerBase>
                <div className="communities-archive-grid__content">
                    {snap.visibleCommunities.map((community) => (
                        <DestinationCard
                            key={community.id}
                            id={community.id}
                            title={community.name}
                            url={community.url}
                            image={{
                                url: community.image || assetPath("/images/placeholder/destination-placeholder.svg"),
                                alt: community.name,
                            }}
                            imageSize={{width: 302, height: 410}}
                        />
                    ))}
                </div>

                {snap.showButton && (
                    <div className="communities-archive-grid__load-more">
                        <ButtonBase as="button" variant="secondary-outline" size="sm" width="auto" onClick={() => model.loadMore()}>
                            {`${t("showMore")} (${snap.visibleCommunities.length} ${t("of")} ${communities.length})`}
                            <SmartImage
                                src={assetPath("/images/icons/arrow-black-down.svg")}
                                alt="Arrow"
                                width={12}
                                height={10}
                                style={{marginLeft: 8}}
                            />
                        </ButtonBase>
                    </div>
                )}
            </ContainerBase>
        </section>
    );
}
